import { DatePipe } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslatePipe } from '@ngx-translate/core';
import { BucketService } from '../../../services/bucket.service';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { WebsiteDialogComponent } from '../../dialogs/website-dialog/website-dialog.component';
import {
  AlTableApi,
  AlTableColumn,
} from '../../features/table/table.component';
import { StatusIconComponent } from '../../features/status-icon/status-icon.component';

@Component({
  selector: 'app-website-table',
  templateUrl: './website-table.component.html',
  styleUrl: './website-table.component.scss',
})
export class WebsiteTableComponent {
  @Input() title = '';
  @Input() bucketId = '';
  @Input() loading = false;
  @Input() searchText = '';

  get filteredData() {
    const data = this.bucketService._websites.value;
    if (!this.searchText) return data;
    const q = this.searchText.toLowerCase();
    return data.filter((w) => w.url?.toLowerCase().includes(q));
  }

  @ViewChild('table') table!: AlTableApi;

  columns: AlTableColumn[] = [
    { key: 'url', title: 'URL', width: '50%', kind: 'name' },
    {
      key: 'updated_at', kind: 'date',
      title: 'Akt. am',
      map: (i: string) => new DatePipe('de-DE', 'CET').transform(i, 'short'),
    },
    {
      key: 'refresh_cycle',
      title: 'Akt. Rate',
      map: (d: string) => this.t.transform('enums.websiteRefreshRate.' + d),
    },
    {
      key: 'agent_enabled',
      title: 'Agent',
      map: (v: any) => (v === false ? 'Deaktiviert' : 'Durchsuchbar'),
      width: '110px',
    },
    {
      key: 'status',
      title: 'Status',
      component: StatusIconComponent,
      componentInputs: ({ value }) => ({ status: value }),
    },
  ];

  rowActions: any[] = [
    {
      key: 'toggleAgent',
      title: 'Für Agenten deaktivieren / aktivieren',
      icon: 'slash-outline',
    },
    {
      key: 'refresh',
      title: this.t.transform('buttons.refresh'),
      isMultiAction: true,
      icon: 'refresh-outline',
    },
    {
      key: 'delete',
      title: this.t.transform('buttons.delete'),
      isMultiAction: true,
      icon: 'trash-outline',
    },
  ];

  globalActions = [
    {
      key: 'add',
      title: this.t.transform('buttons.add'),
      icon: 'plus-outline',
    },
  ];

  constructor(
    protected bucketService: BucketService,
    protected dialogService: NbDialogService,
    protected nbToastrService: NbToastrService,
    protected t: TranslatePipe
  ) {}

  onEvent(e: { event: string; data: any }) {
    const { action, ids, rows, id, row } = e.data;

    switch (e.event) {
      case 'multiAction':
        if (action.key === 'refresh') {
          this.refreshIds(ids);
          return;
        }

        if (action.key === 'delete') {
          this.deleteIds(ids);
          return;
        }

        break;
      case 'rowAction':
        if (action.key === 'toggleAgent') {
          this.toggleAgentAccess(row);
        }
        if (action.key === 'refresh') {
          this.refreshIds([id]);
        }
        if (action.key === 'delete') {
          this.deleteIds([id]);
        }
        break;
      case 'globalAction':
        if (action.key === 'add') {
          this.dialogService.open(WebsiteDialogComponent, {
            context: {
              bucketId: this.bucketId,
            },
          });
          return;
        }
        break;
      default:
        break;
    }
  }

  refreshIds(ids: string[]) {
    this.table.isLoading = true;
    this.bucketService.refreshWebsite(ids).subscribe(() => {
      this.nbToastrService.success(
        `${ids.length} Webseiten wurden erfolgreich zum aktualisieren eingereiht`,
        'Erfolgreich'
      );
      this.table.deselectAll();
      this.table.isLoading = false;
    });
  }

  /** Website behalten, aber der Agent durchsucht sie nicht mehr — und zurück.
   *
   *  Derselbe Endpunkt wie bei Dateien: geschaltet wird die datatypes-Zeile,
   *  und die gibt es für jede Art von Eintrag.
   */
  toggleAgentAccess(row: any) {
    const datatypeId = row?.datatype_id;
    if (!datatypeId) return;
    const next = row.agent_enabled === false;
    this.bucketService.setFileAgentAccess(datatypeId, { agent_enabled: next }).subscribe({
      next: () => {
        row.agent_enabled = next;
        this.nbToastrService.success(
          next
            ? 'Die Website wird wieder durchsucht.'
            : 'Die Website bleibt erhalten, der Agent durchsucht sie nicht mehr.',
          'Wissensbasis'
        );
      },
      error: () =>
        this.nbToastrService.danger('Änderung fehlgeschlagen', 'Wissensbasis'),
    });
  }

  deleteIds(ids: string[]) {
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'dialogs.confirmDialog.deleteEntries.title',
          message: 'dialogs.confirmDialog.deleteEntries.title',
        },
      })
      .onClose.subscribe((confirmed) => {
        if (confirmed) {
          this.table.isLoading = true;
          this.bucketService.deleteDatatype(ids).subscribe(() => {
            this.table.deselectAll();
            this.table.isLoading = false;
          });
        }
      });
  }
}
